import React, { useMemo, useEffect } from "react";
import { Select, SelectProps } from "@mantine/core";
import { useVenuesContext } from "@/contexts/VenuesContext";
import { LoadingIndicator } from "@/components/generic/LoadingIndicator";

export interface VenueSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Selected venue ID */
  value?: string | null | undefined;

  /** Callback when venue selection changes - receives both venueId and full venue object */
  onChange?: (venueId: string | null, venue?: any) => void;

  /** Placeholder text when no venue is selected */
  placeholder?: string;

  /** Whether to show venues that are inactive/disabled */
  includeInactive?: boolean;

  /** Custom filter function for venues */
  venueFilter?: (venue: any) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text when no venues are available */
  emptyText?: string;
}

export const VenueSelector: React.FC<VenueSelectorProps> = ({
  value,
  onChange,
  placeholder = "Select a venue",
  includeInactive = false,
  venueFilter,
  required = false,
  loadingPlaceholder = "Loading venues...",
  emptyText = "No venues available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  const { venues, loading, isEmpty, lastError } = useVenuesContext();

  // Filter and prepare venue options
  const venueOptions = useMemo(() => {
    let filteredVenues = venues;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredVenues = filteredVenues.filter(
        (venue) => venue.status === 100 // Only show active venues
      );
    }

    // Apply custom filter if provided
    if (venueFilter) {
      filteredVenues = filteredVenues.filter(venueFilter);
    }

    // Convert to select options
    return filteredVenues.map((venue) => ({
      value: venue.id || "",
      label: venue.name || "Unnamed Venue",
      disabled: venue.status === 0, // Disabled venues have status 0
    }));
  }, [venues, includeInactive, venueFilter]);

  // Create a map for quick venue lookup by ID
  const venueMap = useMemo(() => {
    const map = new Map();
    venues.forEach((venue) => {
      if (venue.id) {
        map.set(venue.id, venue);
      }
    });
    return map;
  }, [venues]);

  // Auto-select first venue if required and no value is set
  useEffect(() => {
    if (required && !value && venueOptions.length > 0 && !loading && onChange) {
      const firstVenue = venueOptions[0];
      if (firstVenue && !firstVenue.disabled) {
        const selectedVenue = venueMap.get(firstVenue.value);
        onChange(firstVenue.value, selectedVenue);
      }
    }
  }, [required, value, venueOptions, loading, onChange, venueMap]);

  // Handle onChange with venue object lookup
  const handleChange = (selectedVenueId: string | null) => {
    if (selectedVenueId) {
      const selectedVenue = venueMap.get(selectedVenueId);
      onChange?.(selectedVenueId, selectedVenue);
    } else {
      // When clearing selection, pass null for both ID and venue
      onChange?.(null, undefined);
    }
  };

  // Determine the current state and configure select props accordingly
  const getSelectState = () => {
    // Loading state
    if (loading && venues.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={16} color="green" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading venues",
        disabled: true,
        error: error || "Failed to load venues",
      };
    }

    // Empty state
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: true,
      };
    }

    // Normal state with data
    return {
      data: venueOptions,
      placeholder,
      disabled: disabled || loading,
      rightSection:
        loading && venues.length > 0 ? (
          <LoadingIndicator size={16} color="green" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const selectState = getSelectState();

  // Render the select component
  return (
    <Select
      {...selectProps}
      size={size}
      value={value ?? null} // Convert undefined to null
      onChange={handleChange} // Use our custom handler
      required={required}
      clearable={!required}
      allowDeselect={!required}
      searchable
      error={selectState.error || error}
      data={selectState.data}
      placeholder={selectState.placeholder}
      disabled={selectState.disabled}
      rightSection={selectState.rightSection}
    />
  );
};
